import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { SidebarInfoCard } from '@/components/ui/SidebarInfoCard';
import { Button } from '@/components/ui/Button';
import { ScoreSpinner } from '@/components/ui/ScoreSpinner';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { apiClient } from '@/services/api';
import styles from './styles.module.scss';

export interface PredictionCardProps {
  /** Predicted score for team 1 */
  predictedScoreTeam1?: number;
  /** Predicted score for team 2 */
  predictedScoreTeam2?: number;
  /** Predicted outcome */
  predictedOutcome?: string;
  /** Points earned */
  pointsEarned?: number;
  /** Outcome coefficient */
  outcomeCoefficient?: number | null;
  /** Score coefficient */
  scoreCoefficient?: number | null;
  /** Whether edit button should be shown */
  canEdit?: boolean;
  /** Edit button click handler (desktop popup mode) */
  onEditClick?: () => void;
  /** Inline save handler (mobile inline mode). `oddsScore` is undefined when hideOddsInput=true (odds come from API auto-lookup). */
  onInlineSave?: (scoreTeam1: number, scoreTeam2: number, oddsScore?: number) => Promise<void>;
  /** Called when user cancels/closes the inline form — lets parent return to previous view */
  onClose?: () => void;
  /** Start in edit mode immediately (skip preview) */
  initialEditMode?: boolean;
  /** Team 1 name for inline edit labels */
  team1Name?: string;
  /** Team 2 name for inline edit labels */
  team2Name?: string;
  /** Hide the odds/coefficient input in edit mode (when manual coefficients disabled) */
  hideOddsInput?: boolean;
  /** Render bare edit-mode body (no `.card` wrapper) when the parent provides its own card chrome — used by MatchDetailCard's unified mobile/tablet layout for form takeover. Mobile + isEditing only. */
  noChrome?: boolean;
  /** Match ID for auto odds lookup (when hideOddsInput=true) */
  matchId?: number;
  /** Tournament ID for auto odds lookup (when hideOddsInput=true) */
  tournamentId?: number | string;
  /** Team 1 logo URL */
  team1LogoUrl?: string | null;
  /** Team 2 logo URL */
  team2LogoUrl?: string | null;
  /** Additional className */
  className?: string;
}

/**
 * Prediction card for sidebar.
 * Shows user's prediction for a match with score, outcome and coefficients.
 *
 * Layout modes:
 * - Desktop (≥lg): wrapped in SidebarInfoCard chrome
 * - Mobile/tablet (<lg): own .card + .main + .footer anatomy mirroring MatchDetailCard
 *
 * Edit modes:
 * - Mobile (<lg) with onInlineSave: inline edit
 * - Desktop (≥lg) with onEditClick: popup edit
 */
export const PredictionCard: React.FC<PredictionCardProps> = ({
  predictedScoreTeam1,
  predictedScoreTeam2,
  predictedOutcome,
  pointsEarned,
  outcomeCoefficient,
  scoreCoefficient,
  canEdit,
  onEditClick,
  onInlineSave,
  onClose,
  initialEditMode = false,
  hideOddsInput = false,
  matchId,
  tournamentId,
  team1Name = 'Team 1',
  team2Name = 'Team 2',
  team1LogoUrl,
  team2LogoUrl,
  noChrome = false,
  className,
}) => {
  const { isMobile } = useBreakpoint();
  const { t } = useTranslation(['tournament', 'common']);
  const [isEditing, setIsEditing] = useState(initialEditMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const oddsInputRef = useRef<HTMLInputElement>(null);

  // Auto odds from API (when hideOddsInput=true)
  const [autoOdds, setAutoOdds] = useState<number | null>(null);
  const [autoOddsBookmaker, setAutoOddsBookmaker] = useState<string | null>(null);
  const [autoOddsLoading, setAutoOddsLoading] = useState(false);

  // Form state
  const [editScore1, setEditScore1] = useState(predictedScoreTeam1 ?? 0);
  const [editScore2, setEditScore2] = useState(predictedScoreTeam2 ?? 0);
  const [editOddsScore, setEditOddsScore] = useState(scoreCoefficient ? String(scoreCoefficient) : '');

  // Scroll form into view when editing starts
  const scrollFormIntoView = useCallback(() => {
    if (!formRef.current) return;

    // Small delay to ensure form is rendered
    setTimeout(() => {
      formRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, 100);
  }, []);

  // Scroll into view when editing mode activates
  useEffect(() => {
    if (isEditing) {
      scrollFormIntoView();
    }
  }, [isEditing, scrollFormIntoView]);

  // Reset form when prediction changes externally
  useEffect(() => {
    if (!isEditing) {
      setEditScore1(predictedScoreTeam1 ?? 0);
      setEditScore2(predictedScoreTeam2 ?? 0);
      setEditOddsScore(scoreCoefficient ? String(scoreCoefficient) : '');
    }
  }, [predictedScoreTeam1, predictedScoreTeam2, scoreCoefficient, isEditing]);

  // Fetch auto odds when score changes in edit mode (only when hideOddsInput=true)
  useEffect(() => {
    if (!hideOddsInput || !isEditing || !matchId || !tournamentId) return;

    const scoreKey = `${editScore1}:${editScore2}`;
    setAutoOddsLoading(true);
    setAutoOdds(null);
    setAutoOddsBookmaker(null);

    apiClient.getMatchOdds(String(tournamentId), String(matchId), scoreKey)
      .then(response => {
        const data = response.data?.data;
        if (data && data.odds) {
          setAutoOdds(parseFloat(data.odds));
          setAutoOddsBookmaker(data.bookmaker_name || null);
        }
      })
      .catch(() => {})
      .finally(() => setAutoOddsLoading(false));
  }, [hideOddsInput, isEditing, matchId, tournamentId, editScore1, editScore2]);

  const getOutcomeText = (outcome: string) => {
    switch (outcome) {
      case 'team1_win': return 'W1';
      case 'draw': return 'X';
      case 'team2_win': return 'W2';
      default: return '';
    }
  };

  const getOutcomeFromScores = (s1: number, s2: number): string => {
    if (s1 > s2) return 'team1_win';
    if (s1 < s2) return 'team2_win';
    return 'draw';
  };

  const handleEditClick = () => {
    if (isMobile && onInlineSave) {
      // Mobile: switch to inline edit mode
      setIsEditing(true);
      setError(null);
    } else if (onEditClick) {
      // Desktop: open popup
      onEditClick();
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError(null);
    // Reset to original values
    setEditScore1(predictedScoreTeam1 ?? 0);
    setEditScore2(predictedScoreTeam2 ?? 0);
    setEditOddsScore(scoreCoefficient ? String(scoreCoefficient) : '');
    // Notify parent to return to previous view (e.g., NextMatch card)
    if (onClose) onClose();
  };

  const handleSave = async () => {
    setError(null);

    // TODO: When automatic coefficients are implemented via API-Football,
    // odds_score will be set from API data instead of user input
    let odds: number | undefined;
    if (!hideOddsInput) {
      odds = parseFloat(editOddsScore);
      if (isNaN(odds) || odds < 1.01) {
        setError(t('tournament:coefficientMinError'));
        return;
      }
    }

    if (!onInlineSave) return;

    setIsSubmitting(true);
    try {
      await onInlineSave(editScore1, editScore2, odds);
      setIsEditing(false);
    } catch (err: any) {
      const serverMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      toast.error(serverMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasPrediction = predictedScoreTeam1 !== undefined && predictedScoreTeam2 !== undefined;
  const pointsValue = pointsEarned !== undefined ? parseFloat(String(pointsEarned)) : null;
  const showPoints = pointsValue !== null;
  const formatPoints = (n: number) => `${n > 0 ? '+' : ''}${Number.isInteger(n) ? n : n.toFixed(3).replace(/0+$/, '').replace(/\.$/, '')} pts`;
  const showEditIcon = hasPrediction && canEdit && (onEditClick || onInlineSave);

  const headerAction = (showPoints || showEditIcon) ? (
    <div className={styles.headerActions}>
      {showPoints && <span className={styles.points}>{formatPoints(pointsValue!)}</span>}
      {showEditIcon && (
        <button className={styles.editIconBtn} onClick={handleEditClick} aria-label="Edit prediction">
          <i className="icon-pen" />
        </button>
      )}
    </div>
  ) : undefined;

  // Inline edit form body (shared between mobile chrome and SidebarInfoCard wrapper)
  const editFormBody = (() => {
    const editOutcome = getOutcomeFromScores(editScore1, editScore2);
    return (
      <div className={styles.editForm} ref={formRef}>
        {/* Teams row with logos */}
        <div className={styles.teamsRow}>
          <div className={styles.teamCol}>
            {team1LogoUrl && <img src={team1LogoUrl} alt={team1Name} className={styles.teamLogo} />}
            <span className={styles.teamLabel}>{team1Name}</span>
          </div>
          <span className={styles.vsLabel}>Vs</span>
          <div className={styles.teamCol}>
            {team2LogoUrl && <img src={team2LogoUrl} alt={team2Name} className={styles.teamLogo} />}
            <span className={styles.teamLabel}>{team2Name}</span>
          </div>
        </div>

        {/* Score spinners */}
        <div className={styles.scoreEditRow}>
          <ScoreSpinner value={editScore1} onChange={setEditScore1} min={0} max={20} />
          <div className={styles.scoreSeparator}>:</div>
          <ScoreSpinner value={editScore2} onChange={setEditScore2} min={0} max={20} />
        </div>

        {/* Outcome preview */}
        <div className={styles.outcomePreview}>
          {t('tournament:outcomeLabel', { outcome: getOutcomeText(editOutcome) === 'W1' ? t('tournament:win', { team: team1Name }) :
                  getOutcomeText(editOutcome) === 'W2' ? t('tournament:win', { team: team2Name }) : t('tournament:draw') })}
        </div>

        {/* Score coefficient: manual input or auto odds display */}
        {hideOddsInput && (
          <div className={styles.oddsField}>
            <label className={styles.oddsLabel}>{t('tournament:scoreCoeffLabel')}</label>
            {autoOddsLoading ? (
              <span className={styles.oddsLabelItalic}>{t('common:loading')}</span>
            ) : autoOdds ? (
              <div className={styles.autoOddsInline}>
                <span className={styles.autoOddsValue}>{autoOdds.toFixed(2)}</span>
                {autoOddsBookmaker && <span className={styles.autoOddsBookmaker}>{autoOddsBookmaker}</span>}
              </div>
            ) : (
              <span className={styles.oddsLabelItalic}>
                {t('tournament:oddsNotFound', { score: `${editScore1}:${editScore2}` })}
              </span>
            )}
          </div>
        )}
        {!hideOddsInput && (
          <div className={styles.oddsField}>
            <label className={styles.oddsLabel}>{t('tournament:scoreCoeffLabel')}</label>
            <input
              ref={oddsInputRef}
              type="number"
              step="0.01"
              min="1.01"
              value={editOddsScore}
              onChange={(e) => setEditOddsScore(e.target.value)}
              className={styles.oddsInput}
              disabled={isSubmitting}
            />
          </div>
        )}

        {error && <div className={styles.errorMessage}>{error}</div>}

        <div className={styles.editActions}>
          <Button variant="outlined" size="sm" onClick={handleCancel} disabled={isSubmitting}>
            {t('common:cancel')}
          </Button>
          <Button variant="solid" size="sm" onClick={handleSave} disabled={isSubmitting}>
            {t('common:save')}
          </Button>
        </div>
      </div>
    );
  });

  // ============================================================
  // MOBILE / TABLET CHROME (< lg) — own .card + .main + .footer
  // ============================================================
  if (isMobile) {
    // Inline edit mode on mobile
    if (isEditing) {
      const editBody = (
        <div className={styles.main}>
          <div className={styles.captionRow}>
            <span className={styles.caption}>{t('tournament:yourPrediction')}</span>
            <button
              className={styles.editIconBtn}
              onClick={handleCancel}
              aria-label={t('tournament:closeEditForm')}
            >
              <i className="icon-xmark" />
            </button>
          </div>
          {editFormBody()}
        </div>
      );
      return (
        <>
          {isSubmitting && createPortal(
            <div className="spinner-overlay"><div className="spinner" /></div>,
            document.body
          )}
          {noChrome ? editBody : <div className={`${styles.card} ${className || ''}`}>{editBody}</div>}
        </>
      );
    }

    // Display mode — three sub-states:
    //   • hasPrediction → score body + footer with coefficient meta + points/edit
    //   • !hasPrediction + canEdit → caption-only main + full-width CTA footer (the CTA is the
    //     affordance — no `?` placeholder, no team logos that duplicate the hero card above)
    //   • !hasPrediction + locked → quiet "— : —" placeholder + footer "Не задано"
    return (
      <div className={`${styles.card} ${className || ''}`}>
        <div className={styles.main}>
          <span className={styles.caption}>{t('tournament:yourPrediction')}</span>

          {hasPrediction ? (
            <div className={styles.scoreDisplay}>
              <span className={styles.scoreValue}>
                {predictedScoreTeam1} : {predictedScoreTeam2}
              </span>
              {predictedOutcome && (
                <span className={styles.scoreOutcome}>{getOutcomeText(predictedOutcome)}</span>
              )}
            </div>
          ) : !canEdit ? (
            <div className={styles.scoreDisplay}>
              <span className={styles.scorePlaceholder}>— : —</span>
            </div>
          ) : null}
        </div>

        {hasPrediction ? (
          <div className={styles.footer}>
            <span className={styles.footerLabel}>
              {outcomeCoefficient && t('tournament:outcomeCoeff', { coeff: outcomeCoefficient })}
              {outcomeCoefficient && scoreCoefficient && ' · '}
              {scoreCoefficient && t('tournament:scoreCoeff', { coeff: scoreCoefficient })}
            </span>
            <div className={styles.headerActions}>
              {showPoints && <span className={styles.footerPoints}>{formatPoints(pointsValue!)}</span>}
              {showEditIcon && (
                <button
                  className={styles.editIconBtn}
                  onClick={handleEditClick}
                  aria-label="Edit prediction"
                >
                  <i className="icon-pen" />
                </button>
              )}
            </div>
          </div>
        ) : canEdit ? (
          <div className={styles.footerCta}>
            <Button variant="solid" color="accent" size="md" onClick={handleEditClick}>
              {t('tournament:makePrediction')}
            </Button>
          </div>
        ) : (
          <div className={styles.footer}>
            <span className={styles.footerValue}>{t('tournament:notSetPrediction')}</span>
          </div>
        )}
      </div>
    );
  }

  // ============================================================
  // DESKTOP CHROME (≥ lg) — SidebarInfoCard wrapper as before
  // ============================================================
  if (isEditing) {
    return (
      <>
        {isSubmitting && createPortal(
          <div className="spinner-overlay"><div className="spinner" /></div>,
          document.body
        )}
        <SidebarInfoCard
          title={t('tournament:yourPrediction')}
          headerAction={
            <button
              className={styles.editIconBtn}
              onClick={handleCancel}
              aria-label={t('tournament:closeEditForm')}
            >
              <i className="icon-xmark" />
            </button>
          }
          className={className}
        >
          {editFormBody()}
        </SidebarInfoCard>
      </>
    );
  }

  return (
    <SidebarInfoCard
      title={t('tournament:yourPrediction')}
      headerAction={headerAction}
      className={className}
    >
      <div className={styles.content}>
        {hasPrediction ? (
          <>
            <div className={styles.scoreRow}>
              <span className={styles.score}>
                {predictedScoreTeam1} : {predictedScoreTeam2}
              </span>
              {predictedOutcome && (
                <span className={styles.outcome}>
                  ({getOutcomeText(predictedOutcome)})
                </span>
              )}
            </div>
            {(outcomeCoefficient || scoreCoefficient) && (
              <div className={styles.coefficients}>
                {outcomeCoefficient && <span>{t('tournament:outcomeCoeff', { coeff: outcomeCoefficient })}</span>}
                {outcomeCoefficient && scoreCoefficient && <span>, </span>}
                {scoreCoefficient && <span>{t('tournament:scoreCoeff', { coeff: scoreCoefficient })}</span>}
              </div>
            )}
          </>
        ) : canEdit ? (
          <Button variant="solid" size="sm" onClick={handleEditClick}>
            {t('tournament:makePrediction')}
          </Button>
        ) : (
          <div className={styles.notSet}>{t('tournament:notSetPrediction')}</div>
        )}
      </div>
    </SidebarInfoCard>
  );
};

export default PredictionCard;
